import { useState } from 'react';
import { LayoutGrid, List, Info, Mail, Phone, Globe, ArrowLeft } from 'lucide-react';
import ItineraryHeader from './components/ItineraryHeader';
import TimelineView from './components/TimelineView';
import OverviewView from './components/OverviewView';

export default function Type5FullItineraryItemDetails(props) {

  const [viewMode, setViewMode] = useState('timeline');

  const { 
    itineraryTripDays,
    itinerary,
    pageTheme,
    onBack,
  } = props;

  const {
      packageTrendsPageBackground,
      packageTrendsPageCardBackground,
      packageTrendsPagePrimaryColor,
      packageTrendsPageSecondColor,
      packageTrendsPageThirdColor,
      packageTrendsPageFourthColor,
    } = pageTheme;

  return (
    <div className="min-h-screen bg-[#FDFCFB] font-sans text-gray-900">
      <ItineraryHeader 
        itinerary={itinerary}
        pageTheme={pageTheme} 
      />

      {/* View Switcher */}
      <div className="-sticky top-0 z-40 bg-white/80 backdrop-blur-md border-b border-gray-100 shadow-sm">
        <div className="max-w-6xl mx-auto px-6 py-4 md:py-0 h-auto md:h-16 md:flex items-center justify-between">
          <div className="md:flex items-center gap-4">
            {
              onBack &&
              <div className="flex items-center mb-3 md:mb-0">
                <button
                  onClick={onBack}
                  className="p-2 border-none rounded-full bg-gray-100 md:bg-white hover:bg-gray-100 text-gray-500 transition-colors"
                  title="Back to Home"
                >
                    <ArrowLeft className="w-5 h-5" />
                </button>
                <span className="md:hidden text-sm font-bold" style={{marginLeft: 10, color: "rgba(0,0,0,0.7)"}}>
                    Trending Itineraries</span>
              </div>
            }
            <div className="flex gap-1 p-1 bg-gray-100 rounded-xl">
              <button
                onClick={() => setViewMode('timeline')}
                className={`flex border-none items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold transition-all ${
                  viewMode === 'timeline' 
                    ? `bg-white text-[${packageTrendsPagePrimaryColor}] shadow-sm` 
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <List className="w-4 h-4" />
                Timeline
              </button>
              <button
                onClick={() => setViewMode('overview')}
                className={`flex border-none items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold transition-all ${
                  viewMode === 'overview' 
                    ? `bg-white text-[${packageTrendsPagePrimaryColor}] shadow-sm` 
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <LayoutGrid className="w-4 h-4" />
                Overview
              </button>
              <button
                onClick={() => setViewMode('details')}
                className={`flex border-none items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold transition-all ${
                  viewMode === 'details' 
                    ? `bg-white text-[${packageTrendsPagePrimaryColor}] shadow-sm` 
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <Info className="w-4 h-4" />
                Details
              </button>
            </div>
          </div>

          <div className="-hidden -md: flex items-center mt-2 md:mt-0 gap-4 text-xs -font-bold -uppercase tracking-widest text-gray-400">
            {
              itinerary.package_id && <div style={{padding: "15px 20px", fontSize: 13, backgroundColor: packageTrendsPagePrimaryColor, color: packageTrendsPageSecondColor, cursor: "pointer"}}>
                {(itinerary?.checkout_button_text || "Book Package")}
              </div>
            }
            {/*<div className="w-1 h-1 rounded-full bg-gray-300" />
              <span>{itinerary.destination}</span>*/}
          </div>
          </div>
      </div>

      {/* Main Content */}
      <main style={{width: "initial"}} className="relative">
          {viewMode === 'timeline' && (
            <div
              key="timeline"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.3 }}
            >
              <TimelineView 
                itinerary={itinerary} 
                pageTheme={pageTheme}
              />
            </div>
          )}

          {viewMode === 'overview' && (
            <div
              key="overview"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.3 }}
            >
              <OverviewView 
                itinerary={itinerary} 
                pageTheme={pageTheme}
              />
            </div>
          )}

          {viewMode === 'details' && (
            <div
              key="details"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.3 }}
              className="max-w-4xl mx-auto px-6 py-12"
            >
              <div className="bg-white p-8 md:p-12 rounded-3xl border border-gray-100 shadow-sm space-y-12">
                <div>
                  <h2 className="text-3xl font-bold mb-6 font-serif">About this trip</h2>
                  <div 
                    className="prose prose-orange max-w-none text-gray-600 leading-relaxed"
                    dangerouslySetInnerHTML={{ __html: itinerary.body_message }}
                  />
                </div>

                <div className="pt-12 border-t border-gray-100">
                  <h3 className="text-xl font-bold mb-6 font-serif">Contact & Support</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <div className="space-y-4">
                      <div 
                        className="text-gray-600 leading-relaxed"
                        dangerouslySetInnerHTML={{ __html: itinerary.salutation }}
                      />
                    </div>
                    <div className="space-y-4">
                      <div className="flex items-center gap-3 text-gray-500">
                        <Mail style={{color: packageTrendsPagePrimaryColor}} className="w-5 h-5 text-orange-500" />
                        <span>k.benzimah@gmail.com</span>
                      </div>
                      <div className="flex items-center gap-3 text-gray-500">
                        <Phone style={{color: packageTrendsPagePrimaryColor}} className="w-5 h-5 text-orange-500" />
                        <span>+23920291002</span>
                      </div>
                      <div className="flex items-center gap-3 text-gray-500">
                        <Globe style={{color: packageTrendsPagePrimaryColor}} className="w-5 h-5 text-orange-500" />
                        <span>tripbespoke.com</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
      </main>

      {/* Footer 
      <footer className="bg-gray-900 text-white py-20 px-6">
        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-4 gap-12">
          <div className="col-span-1 md:col-span-2">
            <h2 className="text-2xl font-bold mb-6">Trip Bespoke</h2>
            <p className="text-gray-400 max-w-md leading-relaxed">
              Curating exclusive travel experiences across Ghana and beyond. 
              Our mission is to provide seamless, enriching, and unforgettable journeys.
            </p>
          </div>
          <div>
            <h4 className="text-xs font-bold uppercase tracking-widest text-gray-500 mb-6">Quick Links</h4>
            <ul className="space-y-4 text-sm font-medium text-gray-400">
              <li className="hover:text-white cursor-pointer transition-colors">My Itineraries</li>
              <li className="hover:text-white cursor-pointer transition-colors">Support Center</li>
              <li className="hover:text-white cursor-pointer transition-colors">Terms of Service</li>
              <li className="hover:text-white cursor-pointer transition-colors">Privacy Policy</li>
            </ul>
          </div>
          <div>
            <h4 className="text-xs font-bold uppercase tracking-widest text-gray-500 mb-6">Newsletter</h4>
            <div className="flex gap-2">
              <input 
                type="email" 
                placeholder="Email address" 
                className="bg-white/10 border border-white/20 rounded-lg px-4 py-2 text-sm w-full focus:outline-none focus:border-orange-500"
              />
              <button className="bg-orange-600 hover:bg-orange-700 px-4 py-2 rounded-lg text-sm font-bold transition-colors">
                Join
              </button>
            </div>
          </div>
        </div>
        <div className="max-w-6xl mx-auto mt-20 pt-8 border-t border-white/10 flex flex-col md:flex-row justify-between items-center gap-4 text-xs text-gray-500 font-bold uppercase tracking-widest">
          <span>© 2026 Trip Bespoke. All rights reserved.</span>
          <div className="flex gap-6">
            <span>Instagram</span>
            <span>Twitter</span>
            <span>LinkedIn</span>
          </div>
        </div>
      </footer>*/}
    </div>
  );
}
